import React from "react";
import { FaSearchLocation, FaCubes, FaBrain, FaRocket } from "react-icons/fa";
import "./EcosystemIntro.css";

const EcosystemIntro = () => {
  // Evolution story: Current tools → parallel improvements → custom ecosystem → fully owned replacement
  const steps = [
    {
      number: 1,
      title: "Start From Your Current Tools",
      description:
        "We begin with what you already use — ERP, SaaS, spreadsheets, or custom tools. Zero disruption, and your team keeps working as normal.",
      icon: FaSearchLocation,
    },
    {
      number: 2,
      title: "Parallel Gains, No Disruption",
      description:
        "We build add-ons and automation around your existing setup — removing manual work, reducing errors, and speeding up operations immediately.",
      icon: FaCubes,
    },
    {
      number: 3,
      title: "Evolve Into Your Custom Ecosystem",
      description:
        "Those improvements expand into one connected platform: Sales, Inventory, Accounting, Logistics, Reporting — unified, real-time, and tailored to your workflows.",
      icon: FaBrain,
    },
    {
      number: 4,
      title: "Replace & Own Everything",
      description:
        "Once proven, your ecosystem replaces legacy tools step-by-step — no subscriptions, no vendor lock-in. You fully own the system and your data.",
      icon: FaRocket,
    },
  ];

  const benefits = [
    "One unified platform — Web + iOS + Android with smooth native-like experience",
    "A digital ecosystem designed around your workflows (not generic templates)",
    "AI that improves using your operational data — smarter every month",
    "Faster decisions, fewer errors, higher profitability across teams",
  ];

  return (
    <section className="ecosystem-intro-section">
      <div className="ecosystem-container">
        {/* Headline Section */}
        <div className="headline-section">
          <div className="headline-badge">
            <FaRocket className="badge-icon" />
            <span>TRANSFORMATION PATHWAY</span>
          </div>
          <h2 className="flow-heading">Your Journey to Own Ecosystem</h2>
          <div className="headline-flow">
            <div className="flow-step">
              <div className="step-check">✓</div>
              <div className="step-text">Start At Current</div>
            </div>
            <div className="flow-arrow">→</div>
            <div className="flow-step">
              <div className="step-check">✓</div>
              <div className="step-text">Get Benefits</div>
            </div>
            <div className="flow-arrow">→</div>
            <div className="flow-step">
              <div className="step-check">✓</div>
              <div className="step-text">Migrate Steadily</div>
            </div>
            <div className="flow-arrow">→</div>
            <div className="flow-step highlight-step">
              <div className="step-check">✓</div>
              <div className="step-text">Own Everything</div>
            </div>
          </div>
        </div>

        {/* Introduction Text */}
        <div className="intro-text">
          <div className="intro-card problem-card">
            <div className="intro-icon-wrapper problem-icon">
              <FaBrain className="intro-icon" />
            </div>
            <div className="intro-content">
              <h3 className="intro-card-title">The Problem</h3>
              <p className="intro-card-text">
                Most businesses are trapped in legacy systems that slow everything down. Instead of
                10× efficiency, teams fight complexity, delays, and workarounds.
              </p>
            </div>
          </div>

          <div className="intro-card solution-card">
            <div className="intro-icon-wrapper solution-icon">
              <FaCubes className="intro-icon" />
            </div>
            <div className="intro-content">
              <h3 className="intro-card-title">Our Approach</h3>
              <p className="intro-card-text">
                No rip-and-replace. We evolve what you use today, deliver benefits in parallel, and
                end with a system you fully own — built around your workflows.
              </p>
            </div>
          </div>
        </div>

        {/* Build Section */}
        <div className="build-section">
          <h3 className="section-title">How We Take You From “Current” to “Owned”</h3>

          <div className="steps-grid">
            {steps.map((step) => {
              const IconComponent = step.icon;
              return (
                <div key={step.number} className="step-card">
                  <div className="step-icon-wrapper">
                    <IconComponent className="step-icon" />
                  </div>

                  <div className="step-content">
                    <div className="step-header">
                      <span className="step-number">Phase {step.number}</span>
                      <h4 className="step-title">{step.title}</h4>
                    </div>
                    <p className="step-description">{step.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* What You Get Section */}
        <div className="benefits-section">
          <h3 className="section-title">What You Get</h3>
          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className="benefit-item">
                <span className="checkmark">✔</span>
                <p className="benefit-text">{benefit}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Line */}
        <div className="bottom-line-section">
          <h3 className="bottom-line-title">Bottom Line</h3>
          <p className="bottom-line-text">
            Your business is unique — your software ecosystem should be too.
          </p>
          <p className="bottom-line-tagline">
            We don’t force your business into software —
            <br />
            <span className="tagline-emphasis">we build software around your business.</span>
          </p>

          <div className="bottom-line-cta">
            <p className="cta-text">“Stop renting tools. Start owning your business ecosystem.”</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EcosystemIntro;
